// Settings › General › Automatic dormancy (UC-PATIENTS-029; PAT-54, Q-18; BR-PATIENTS-040): the organisation's rule, off unless
// the Owner switches it on, with the months a patient may go without a visit, invoice, appointment or status change before the
// nightly run makes them Dormant. The Owner edits it here (PUT /settings/org/patients, audited as any settings change); every other
// role reads the rule in force with "The Owner changes this". A branch's own value (a branch override) is named, not edited here.
import { AUTO_DORMANCY_MONTHS } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { settingsScope } from "../api/endpoints";
import { describeError, isApiError } from "../api/problem";
import { canEverywhere, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, ErrorAlert, Field, KV, Loading, Panel, TextInput } from "../ui/bits";

const MONTHS_MESSAGE = `A whole number of months from ${AUTO_DORMANCY_MONTHS.min} to ${AUTO_DORMANCY_MONTHS.max}.`;

/** "24" → 24; anything but a whole number in range → null. */
export function parseMonths(text: string): number | null {
  if (!/^\d{1,3}$/.test(text.trim())) return null;
  const n = Number(text.trim());
  return n >= AUTO_DORMANCY_MONTHS.min && n <= AUTO_DORMANCY_MONTHS.max ? n : null;
}

export function AutoDormancyPanel() {
  const s = useSessionView();
  const qc = useQueryClient();
  const block = useWriteBlock();
  const owner = canEverywhere(s, "tenancy.settings.write");
  const q = useQuery({ queryKey: ["settings", "org", "patients"], queryFn: () => settingsScope.get("org", "patients") });
  const values = q.data?.data.settings;
  const enabled = values?.autoDormancyEnabled?.value === true;
  const months = typeof values?.autoDormancyMonths?.value === "number" ? values.autoDormancyMonths.value : AUTO_DORMANCY_MONTHS.default;
  const [on, setOn] = useState(false);
  const [text, setText] = useState(String(AUTO_DORMANCY_MONTHS.default));
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; error?: unknown } | null>(null);
  useEffect(() => { if (values) { setOn(enabled); setText(String(months)); } }, [values, enabled, months]);
  const parsed = parseMonths(text);
  // Checked when Save is pressed (E1), never while the user is still typing.
  const monthsErr = checked && parsed === null ? MONTHS_MESSAGE : isApiError(result?.error) ? result.error.field("autoDormancyMonths")?.message : undefined;
  const dirty = on !== enabled || text !== String(months);

  const save = async () => {
    setChecked(true);
    if (parsed === null || busy) return;
    setBusy(true);
    setResult(null);
    try {
      await settingsScope.put("org", "patients", { autoDormancyEnabled: on, autoDormancyMonths: parsed });
      await qc.invalidateQueries({ queryKey: ["settings", "org", "patients"] });
      setChecked(false);
      setResult({ ok: true });
    } catch (err) { setResult({ ok: false, error: err }); }
    finally { setBusy(false); }
  };

  const rule = enabled ? `On — after ${months} months without a visit` : "Off";
  return (
    <Panel title="Automatic dormancy" meta="PAT-54 · runs nightly at 01:00" foot={<span className="help">Dormant patients get no recalls, reminders or marketing. Nothing is made Active again automatically — staff reactivate a patient when they return.</span>}>
      {q.isPending && <Loading what="the dormancy rule" />}
      {q.isError && <ErrorAlert error={q.error} />}
      {values && !owner && (
        <KV rows={[
          ["Rule", rule],
          ["What counts", "A visit, an invoice, any appointment (kept, missed or booked) or a status change; for a patient captured here, the day they were registered"],
          ["Changed by", "The Owner changes this"],
        ]} />
      )}
      {values && owner && (
        <form className="stack gap-8" noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
          {result?.ok && <Alert tone="ok" role="status"><b>Saved.</b> {on ? `Patients inactive for more than ${parsed} months are made Dormant from tonight.` : "Nobody is made Dormant automatically."} The change is in the audit log.</Alert>}
          {result && !result.ok && !monthsErr && <Alert tone="crit"><b>Not saved.</b> {describeError(result.error)}</Alert>}
          <div className="field">
            <label id="dormancy-rule">Make long-inactive patients Dormant</label>
            <div className="stack gap-4" role="radiogroup" aria-labelledby="dormancy-rule">
              <label className="check"><input type="radio" name="dormancy-on" checked={!on} onChange={() => setOn(false)} /><span className={`box radio${!on ? " on" : ""}`} /><span>Off — staff set Dormant by hand</span></label>
              <label className="check"><input type="radio" name="dormancy-on" checked={on} onChange={() => setOn(true)} /><span className={`box radio${on ? " on" : ""}`} /><span>On — every night, by the rule below</span></label>
            </div>
          </div>
          <Field label="Months without a visit" htmlFor="dormancy-months" error={monthsErr} help={`Whole months, ${AUTO_DORMANCY_MONTHS.min} to ${AUTO_DORMANCY_MONTHS.max}; ${AUTO_DORMANCY_MONTHS.default} suggested. Less than a year would catch patients on an ordinary one-year recall.`}>
            <TextInput id="dormancy-months" inputMode="numeric" maxLength={3} value={text} onChange={(e) => { setChecked(false); setText(e.target.value.replace(/\D/g, "")); }} aria-invalid={monthsErr ? true : undefined} />
          </Field>
          <div className="row c gap-8">
            <button className="btn primary" type="submit" disabled={busy || !dirty || block !== null} title={block ? WRITE_BLOCK_TITLE[block] : undefined}>{busy ? "Saving…" : "Save rule"}</button>
            <span className="help">Now: {rule}. A branch may carry its own value.</span>
          </div>
        </form>
      )}
    </Panel>
  );
}
